// Surfaces tab — a responsive grid of heatmap panels, one per output, with
// sample-point overlays and click-to-queue manual sampling. When the campaign
// runs in oracle mode, each panel switches to a Truth / Estimate / Diff
// triptych.

import { useQuery } from '@tanstack/react-query';
import type { MouseEvent, ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  useCampaignStateQuery,
  useCampaignStream,
  useManualSampleMutation,
  fetchSurface,
} from '../api';
import {
  HeatmapPanel,
  PointOverlay,
  PulseMarker,
  Triptych,
  adaptiveSamplingTheme,
} from './_primitiveBridge';
import type { AxisSpec, SamplePoint, SliceState, SurfaceResponse } from '../types';

import { useElementSize } from '../../../hooks/useElementSize';
import { ManualSamplePopover } from './ManualSamplePopover';
import { SliceControls } from './SliceControls';
import { projectPointsToSlice } from './projectPointsToSlice';
import { useSliceState } from './useSliceState';

export type SurfacesTabProps = {
  campaignId: string;
};

type PendingManual = {
  output: string;
  inputs: Record<string, number>;
  clientX: number;
  clientY: number;
};

const LG_BREAKPOINT_PX = 1024;

function buildFullInputs(
  axes: readonly AxisSpec[],
  slice: SliceState,
  xValue: number,
  yValue: number,
): Record<string, number> {
  const inputs: Record<string, number> = {};
  for (const axis of axes) {
    if (axis.mode === 'fixed') {
      inputs[axis.name] = axis.fixedValue ?? 0;
      continue;
    }
    if (axis.name === slice.xInput) {
      inputs[axis.name] = xValue;
    } else if (axis.name === slice.yInput) {
      inputs[axis.name] = yValue;
    } else {
      inputs[axis.name] =
        slice.pinned[axis.name] ??
        0.5 * ((axis.min ?? 0) + (axis.max ?? 0));
    }
  }
  return inputs;
}

function sliceKey(slice: SliceState): string {
  const pinnedEntries = Object.entries(slice.pinned).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  return JSON.stringify({
    x: slice.xInput,
    y: slice.yInput,
    p: pinnedEntries,
    s: Math.round(slice.slabFraction * 1000) / 1000,
  });
}

function buildProposedIds(points: readonly SamplePoint[]): Set<string> {
  // "Proposed" = queued sampler points that haven't started running. Here we
  // take every queued sampler point; if it's actually running the server
  // flips status to 'running' and the set shrinks automatically.
  const ids = new Set<string>();
  for (const p of points) {
    if (p.source === 'sampler' && p.status === 'queued') ids.add(p.id);
  }
  return ids;
}

type PanelBodyProps = {
  output: string;
  surface: SurfaceResponse | undefined;
  isLoading: boolean;
  slice: SliceState;
  points: readonly SamplePoint[];
  proposedIds: Set<string>;
  oracleEnabled: boolean;
  onPanelClick: (evt: MouseEvent<HTMLDivElement>, output: string) => void;
};

type OverlayPoint = {
  x: number;
  y: number;
  iter: number;
  source: 'sampler' | 'manual';
  status: 'queued' | 'running' | 'done';
};

function formatOverlayPoint(
  p: SamplePoint,
  slice: SliceState,
): OverlayPoint | null {
  if (p.status === 'failed') return null;
  return {
    x: p.inputs[slice.xInput] ?? 0,
    y: p.inputs[slice.yInput] ?? 0,
    iter: p.iter,
    source: p.source,
    status: p.status,
  };
}

function PanelBody(props: PanelBodyProps) {
  const {
    output,
    surface,
    isLoading,
    slice,
    points,
    proposedIds,
    oracleEnabled,
    onPanelClick,
  } = props;

  const doneSampler = points.filter(
    (p) => p.source === 'sampler' && p.status === 'done',
  );
  const doneManual = points.filter(
    (p) => p.source === 'manual' && p.status === 'done',
  );
  const queued = points.filter(
    (p) => p.status === 'queued' || p.status === 'running',
  );

  const overlayPoints: OverlayPoint[] = [
    ...doneSampler,
    ...doneManual,
    ...queued,
  ]
    .map((p) => formatOverlayPoint(p, slice))
    .filter((p): p is OverlayPoint => p !== null);

  const proposedBatch = queued
    .filter((p) => proposedIds.has(p.id))
    .map((p, i) => ({
      x: p.inputs[slice.xInput] ?? 0,
      y: p.inputs[slice.yInput] ?? 0,
      rank: i + 1,
    }));

  if (isLoading || !surface) {
    return (
      <div
        style={{
          height: 320,
          display: 'grid',
          placeItems: 'center',
          color: adaptiveSamplingTheme?.textMuted ?? '#888',
          fontSize: 12,
        }}
      >
        Loading surface…
      </div>
    );
  }

  const meanPanel = (
    <div
      style={{ position: 'relative' }}
      onClick={(e) => onPanelClick(e, output)}
      data-testid={`heatmap-panel-${output}`}
    >
      <HeatmapPanel
        x={surface.x}
        y={surface.y}
        z={surface.mean}
        uncertainty={surface.sigma}
        xLabel={slice.xInput}
        yLabel={slice.yInput}
        zLabel={output}
      />
      <PointOverlay points={overlayPoints} proposedBatch={proposedBatch} />
      {queued.map((p) => {
        const x = p.inputs[slice.xInput] ?? 0;
        const y = p.inputs[slice.yInput] ?? 0;
        const variant: 'queued' | 'proposed' | 'manual' =
          p.source === 'manual'
            ? 'manual'
            : proposedIds.has(p.id)
              ? 'proposed'
              : 'queued';
        return (
          <PulseMarker
            key={`pulse-${p.id}`}
            x={x}
            y={y}
            variant={variant}
          />
        );
      })}
    </div>
  );

  if (!oracleEnabled || !surface.truthMean) {
    return meanPanel;
  }

  // Oracle triptych: Truth / Estimate / Truth − Estimate
  const diff: number[][] = surface.truthMean.map((row, i) =>
    row.map((truth, j) => {
      const est = surface.mean[i]?.[j] ?? 0;
      return truth - est;
    }),
  );

  return (
    <Triptych
      panels={[
        {
          title: 'Truth',
          content: (
            <HeatmapPanel
              x={surface.x}
              y={surface.y}
              z={surface.truthMean}
              xLabel={slice.xInput}
              yLabel={slice.yInput}
              zLabel={output}
              highlight="ridge"
            />
          ),
        },
        {
          title: 'Estimate',
          content: (
            <div
              style={{ position: 'relative' }}
              onClick={(e) => onPanelClick(e, output)}
              data-testid={`heatmap-panel-${output}-estimate`}
            >
              <HeatmapPanel
                x={surface.x}
                y={surface.y}
                z={surface.mean}
                uncertainty={surface.sigma}
                xLabel={slice.xInput}
                yLabel={slice.yInput}
                zLabel={output}
              />
              <PointOverlay points={overlayPoints} proposedBatch={proposedBatch} />
            </div>
          ),
        },
        {
          title: 'Truth − Estimate',
          content: (
            <HeatmapPanel
              x={surface.x}
              y={surface.y}
              z={diff}
              xLabel={slice.xInput}
              yLabel={slice.yInput}
              zLabel={`${output} (Δ)`}
            />
          ),
        },
      ]}
    />
  );
}

export function SurfacesTab(props: SurfacesTabProps): ReactElement {
  const { campaignId } = props;
  const stateQuery = useCampaignStateQuery(campaignId);

  // Subscribe to the live event stream; the stream hook invalidates the
  // state query internally when new data arrives.
  useCampaignStream(campaignId);

  const campaignState = stateQuery.data;
  const inputs = campaignState?.config.inputs ?? [];
  const outputs = campaignState?.config.outputs ?? [];
  const oracleEnabled = campaignState?.config.oracle.enabled === true;

  const { slice, setSlice } = useSliceState(campaignId, inputs);

  const key = useMemo(() => sliceKey(slice), [slice]);

  // Filter sample points to those within the current slab.
  const onSlicePoints = useMemo(
    () => projectPointsToSlice(campaignState?.points ?? [], slice, inputs),
    [campaignState?.points, slice, inputs],
  );
  const proposedIds = useMemo(
    () => buildProposedIds(campaignState?.points ?? []),
    [campaignState?.points],
  );

  // Manual-sample state: anchor + payload for the popover.
  const [pending, setPending] = useState<PendingManual | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const manualMutation = useManualSampleMutation(campaignId);

  // FXC-9004: the grid column count used to read window.innerWidth inline at
  // render with no resize subscription, so it froze at the last render width
  // and measured the window rather than the panel. Measure the grid container
  // and go two-up only when the PANEL is wide enough.
  const [gridRef, gridSize] = useElementSize<HTMLDivElement>();
  const twoColumns = gridSize.width >= LG_BREAKPOINT_PX;

  // Reset pending when the campaign changes.
  useEffect(() => {
    setPending(null);
  }, [campaignId]);

  const handlePanelClick = (evt: MouseEvent<HTMLDivElement>, output: string) => {
    // Convert click coordinates back to data-space using the panel bounds.
    const rect = evt.currentTarget.getBoundingClientRect();
    const fx = (evt.clientX - rect.left) / Math.max(1, rect.width);
    const fy = 1 - (evt.clientY - rect.top) / Math.max(1, rect.height);

    const xAxis = inputs.find((a) => a.name === slice.xInput);
    const yAxis = inputs.find((a) => a.name === slice.yInput);
    if (!xAxis || !yAxis || xAxis.min === undefined || xAxis.max === undefined) {
      return;
    }
    if (yAxis.min === undefined || yAxis.max === undefined) return;

    const xValue = xAxis.min + fx * (xAxis.max - xAxis.min);
    const yValue = yAxis.min + fy * (yAxis.max - yAxis.min);

    const fullInputs = buildFullInputs(inputs, slice, xValue, yValue);
    setPending({
      output,
      inputs: fullInputs,
      clientX: evt.clientX,
      clientY: evt.clientY,
    });
  };

  const confirmManual = () => {
    if (!pending) return;
    manualMutation.mutate(pending.inputs);
    setPending(null);
  };

  const cancelManual = () => setPending(null);

  // Position the invisible anchor span at the click location.
  useEffect(() => {
    if (!pending || !anchorRef.current) return;
    const node = anchorRef.current;
    node.style.position = 'fixed';
    node.style.left = `${pending.clientX}px`;
    node.style.top = `${pending.clientY}px`;
    node.style.width = '1px';
    node.style.height = '1px';
    node.style.pointerEvents = 'none';
  }, [pending]);

  if (stateQuery.isLoading || !campaignState) {
    return (
      <div style={{ padding: 16, color: adaptiveSamplingTheme?.textMuted ?? '#888' }}>
        Loading campaign…
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 12,
        background: adaptiveSamplingTheme?.background ?? '#08090b',
        color: adaptiveSamplingTheme?.text ?? '#e6e6e6',
        fontFamily: adaptiveSamplingTheme?.fontStack ?? 'system-ui, sans-serif',
      }}
      data-testid="surfaces-tab"
    >
      <SliceControls inputs={inputs} slice={slice} onChange={setSlice} />

      <div
        ref={gridRef}
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: twoColumns ? '1fr 1fr' : '1fr',
        }}
        data-testid="surfaces-grid"
      >
        {outputs.map((output) => (
          <OutputPanel
            key={output.name}
            campaignId={campaignId}
            outputName={output.name}
            slice={slice}
            sliceKey={key}
            points={onSlicePoints}
            proposedIds={proposedIds}
            oracleEnabled={oracleEnabled}
            onPanelClick={handlePanelClick}
          />
        ))}
      </div>

      <span ref={anchorRef} aria-hidden />
      {pending && (
        <ManualSamplePopover
          open={true}
          inputs={pending.inputs}
          axes={inputs}
          anchor={anchorRef}
          onConfirm={confirmManual}
          onCancel={cancelManual}
        />
      )}
    </div>
  );
}

type OutputPanelProps = {
  campaignId: string;
  outputName: string;
  slice: SliceState;
  sliceKey: string;
  points: readonly SamplePoint[];
  proposedIds: Set<string>;
  oracleEnabled: boolean;
  onPanelClick: (evt: MouseEvent<HTMLDivElement>, output: string) => void;
};

function OutputPanel(props: OutputPanelProps): ReactElement {
  const {
    campaignId,
    outputName,
    slice,
    sliceKey: key,
    points,
    proposedIds,
    oracleEnabled,
    onPanelClick,
  } = props;

  const surfaceQuery = useQuery<SurfaceResponse>({
    queryKey: ['surface', campaignId, outputName, key],
    queryFn: () =>
      fetchSurface(campaignId, {
        output: outputName,
        xInput: slice.xInput,
        yInput: slice.yInput,
        slice: slice.pinned,
      }),
    enabled: Boolean(slice.xInput) && Boolean(slice.yInput),
    staleTime: 5_000,
  });

  return (
    <div
      style={{
        background: adaptiveSamplingTheme?.panel ?? '#0d0f12',
        border: '1px solid #1f2328',
        borderRadius: 6,
        padding: 8,
      }}
      data-testid={`output-panel-${outputName}`}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 6,
          color: adaptiveSamplingTheme?.text ?? '#e6e6e6',
        }}
      >
        {outputName}
      </div>
      <PanelBody
        output={outputName}
        surface={surfaceQuery.data}
        isLoading={surfaceQuery.isLoading}
        slice={slice}
        points={points}
        proposedIds={proposedIds}
        oracleEnabled={oracleEnabled}
        onPanelClick={onPanelClick}
      />
    </div>
  );
}
